import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
  IconButton,
  Box,
  Grid,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import BusinessIcon from '@mui/icons-material/Business';
import salesService from '../../../../../services/salesService';
import { useAuth, Branch } from '../../../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface AddPykScreenModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  onSaved?: () => void;
}

const AddPykScreenModal: React.FC<AddPykScreenModalProps> = ({ open, onClose, companyId, onSaved }) => {
  const { t } = useTranslation('salesSystem');
  const { branches } = useAuth(); // ✅ AuthContext'ten branches alınıyor
  const [pykScreenName, setPykScreenName] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Modal kapandığında state'leri sıfırla
  useEffect(() => {
    if (!open) {
      setSelectedBranch(null);
      setPykScreenName('');
      setError(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!pykScreenName.trim()) {
      setError(t('quickSaleModals.addPyk.errors.screenNameRequired'));
      return;
    }
    if (!companyId) {
      setError(t('quickSaleModals.addPyk.errors.companyNotFound'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // SQL'e kaydet
      await salesService.createPykScreen({
        Name: pykScreenName.trim(),
        BranchId: selectedBranch?.id || null,
        SettingsJson: null // Başlangıçta boş
      });

      console.log("Yeni PYK Ekranı SQL'e kaydedildi");
      onSaved?.();
      handleClose();
    } catch (err: any) {
      console.error("PYK ekranı eklenirken hata:", err);
      setError(err.response?.data?.Message || t('quickSaleModals.addPyk.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // State'leri sıfırla modal kapanırken
    setPykScreenName('');
    setSelectedBranch(null);
    setError(null);
    setSaving(false);
    onClose(); // Ana bileşene kapatma bilgisini gönder
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {t('quickSaleModals.addPyk.titleCreate')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {/* Hata Mesajı Alanı */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)} // Hata mesajını kapatma butonu
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Ekran Adı */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('quickSaleModals.addPyk.fields.screenNameLabel')}
              value={pykScreenName}
              onChange={(e) => setPykScreenName(e.target.value)}
              required
              InputProps={{
                startAdornment: <PointOfSaleIcon color="action" sx={{ mr: 1 }} />
              }}
              helperText={t('quickSaleModals.addPyk.fields.screenNameHelper')}
            />
          </Grid>

          {/* Atanan Şube */}
          <Grid item xs={12}>
            <Autocomplete
              options={branches.filter(b => b.id !== "null")} // "Şubeye Ait Değil" hariç
              getOptionLabel={(option) => option.name}
              value={selectedBranch}
              onChange={(_, newValue) => setSelectedBranch(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('quickSaleModals.addPyk.fields.assignedBranchLabel')}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <BusinessIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          {t('quickSaleModals.common.cancel')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!pykScreenName.trim() || saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? t('quickSaleModals.common.creating') : t('quickSaleModals.common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPykScreenModal; 